/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { PAGE_BOTTOM_MARGIN } from "constants/constants";
import {
  getPageHeight,
  getPageWidth,
  getRowIndex,
  getRowHeight,
  getPagesInRow,
  getTotalRows,
  isLeftPageIndex
} from "selectors/pageSelectors";
import { getPagesViewYPos, getPagesViewXPos, getGoToPageIndex } from "selectors/pagedViewState";
import {
  getTotalPages,
  isSinglePageMode,
  isTwoColumnViewMode,
  getColumnMaxWidth,
  isFullScreenOrFitOnePageMode
} from "selectors/documentSelectors";
import {
  getViewPortHeightWithoutPagesViewScrollbar,
  getDocumentVisibleHeight,
  getViewPortWidthWithoutPagesViewScrollbar
} from "selectors/documentViewSelectors";
import * as constants from "constants/constants";
import { getPageZoomLevel } from "selectors/zoom";
import { getHitElement } from "utils/DomUtils";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import PageCanvasCss from "components/document/page/PageCanvas.css";
import { getTopPanelHeight, getTopPanelVisibility } from "./topPanelSelectors";

export const getPageBeforeOrAtOffset = (stores, offsetFromTop = 0) => {
  if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
    return {
      pageIndex: getCurrentPageIndex(stores),
      pageBottomMargin: -1,
      yInPage: 0
    };
  }

  const topPanelOffset = getTopPanelVisibility(stores) ? getTopPanelHeight() : 0;
  let currY = getPagesViewYPos(stores) + offsetFromTop - constants.PAGES_TOP_PADDING - topPanelOffset;
  currY = Math.ceil(currY);

  const totalRows = getTotalRows(stores);
  const totalPages = getTotalPages(stores);

  let rowIndex = 0;
  let currPageHeight = -1;
  while (rowIndex < totalRows) {
    currPageHeight = getRowHeight(stores, rowIndex) + PAGE_BOTTOM_MARGIN;
    if (currY >= currPageHeight) {
      currY -= currPageHeight;
      rowIndex++;
    } else {
      break;
    }
  }

  const pagesInRow = getPagesInRow(stores, rowIndex);
  let i = pagesInRow.leftPageIndex >= 0 ? pagesInRow.leftPageIndex : 0;

  let xInPage = -1;
  if (isTwoColumnViewMode(stores)) {
    if (i < totalPages - 1 && pagesInRow.leftPageIndex > -1) {
      let currX = getPagesViewXPos(stores);
      currX = Math.ceil(currX);
      if (currX >= getColumnMaxWidth(stores)) {
        i += 1;
        xInPage = currX;
      } else if (currX >= 0) {
        xInPage = currX;
      }
    }
  }

  let pageBottomMargin = -1;
  let yInPage = -1;
  if (i < getTotalPages(stores)) {
    if (currY >= 0 && currY <= getPageHeight(stores, i)) {
      yInPage = currY;
    } else if (currY >= 0) {
      pageBottomMargin = currY - getPageHeight(stores, i);
    }
  } else {
    if (currY >= 0) {
      pageBottomMargin = currY + PAGE_BOTTOM_MARGIN;
    }
    i = getTotalPages(stores) - 1;
  }
  return {
    pageIndex: i,
    pageBottomMargin,
    yInPage,
    xInPage
  };
};

export const getCurrentViewState = stores => {
  let { pageIndex, pageBottomMargin, yInPage } = getPageBeforeOrAtOffset(stores);
  if (yInPage !== -1) {
    yInPage /= getPageZoomLevel(stores, pageIndex);
  }
  let xInPage = getPagesViewXPos(stores) / getPageZoomLevel(stores, pageIndex);
  return { pageIndex, xInPage, yInPage, pageBottomMargin };
};

const getOverlappingInterval = (start1, end1, start2, end2) => {
  if (end1 < start2 || end2 < start1) return 0;

  const overlapStart = Math.max(start1, start2);
  const overlapEnd = Math.min(end1, end2);

  return overlapEnd - overlapStart;
};

const getFractionPageWidthVisible = (stores, pageIndexArray, xInPage) => {
  const viewportStartX = xInPage;
  const viewportEndX = viewportStartX + getViewPortWidthWithoutPagesViewScrollbar(stores);

  const colMaxWidth = getColumnMaxWidth(stores);
  return pageIndexArray.map(pageIndex => {
    const pageWidth = getPageWidth(stores, pageIndex);
    const pageStartX = isLeftPageIndex(stores, pageIndex) ? colMaxWidth - pageWidth : colMaxWidth;
    const pageEndX = pageStartX + pageWidth;
    return pageWidth > 0 ? getOverlappingInterval(pageStartX, pageEndX, viewportStartX, viewportEndX) / pageWidth : 0;
  });
};

const getFractionPageHeightVisible = (stores, pageIndexArray, yInPage) => {
  const viewportStartY = yInPage;
  const viewportEndY = viewportStartY + getViewPortHeightWithoutPagesViewScrollbar(stores);

  const topRow = getRowIndex(stores, pageIndexArray[0]);
  const topRowHeight = getRowHeight(stores, topRow);
  return pageIndexArray.map((pageIndex, index) => {
    const pageHeight = getPageHeight(stores, pageIndex);
    const rowIndex = getRowIndex(stores, pageIndex);
    const rowHeight = getRowHeight(stores, rowIndex);

    const isTopRow = index < 2;
    const pageStartY = (rowHeight - pageHeight) / 2 + (isTopRow ? 0 : topRowHeight + PAGE_BOTTOM_MARGIN);
    const pageEndY = pageStartY + pageHeight;
    return pageHeight > 0 ? getOverlappingInterval(pageStartY, pageEndY, viewportStartY, viewportEndY) / pageHeight : 0;
  });
};

const getCurrentPageIndexTwoColumn = (stores, pageIndex, yInPage, pageBottomMargin, xInPage) => {
  const totalPages = getTotalPages(stores);
  if (pageIndex < totalPages - 1) {
    const topRow = getRowIndex(stores, pageIndex);
    const pagesInTopRow = getPagesInRow(stores, topRow);
    const pagesInBottomRow = getPagesInRow(stores, topRow + 1);
    const pageIndexArray = [
      pagesInTopRow.leftPageIndex,
      pagesInTopRow.rightPageIndex,
      pagesInBottomRow.leftPageIndex,
      pagesInBottomRow.rightPageIndex
    ];
    const goToPageIndex = getGoToPageIndex(stores);
    const pageWidthFractionArray = getFractionPageWidthVisible(stores, pageIndexArray, xInPage);
    if (pageBottomMargin !== -1) {
      pageIndex =
        pageWidthFractionArray[3] > pageWidthFractionArray[2]
          ? pagesInBottomRow.rightPageIndex
          : pageWidthFractionArray[2] !== 0
          ? pagesInBottomRow.leftPageIndex
          : xInPage > getColumnMaxWidth(stores) // case when area visible of both bottom pages is 0
          ? pagesInBottomRow.rightPageIndex
          : pagesInBottomRow.leftPageIndex;
      const maxWidthVisible = Math.max(pageWidthFractionArray[3], pageWidthFractionArray[2]);
      if (goToPageIndex !== -1 && pageIndex !== goToPageIndex) {
        if (
          (goToPageIndex === pagesInBottomRow.rightPageIndex || goToPageIndex === pagesInBottomRow.leftPageIndex) &&
          Math.abs(pageWidthFractionArray[3] - pageWidthFractionArray[2]) < 0.01 &&
          maxWidthVisible !== 0
        ) {
          pageIndex = goToPageIndex;
        } else {
          stores.pagedViewState.setGoToPageIndex(-1);
        }
      }
    } else {
      const pageHeightFractionArray = getFractionPageHeightVisible(stores, pageIndexArray, yInPage);
      const pageAreaFractionArray = pageIndexArray.map(
        (pageIndex, index) => pageWidthFractionArray[index] * pageHeightFractionArray[index]
      );

      const highestAreaFractionVisible = Math.max(...pageAreaFractionArray);
      if (highestAreaFractionVisible === 0) {
        pageIndex = xInPage > getColumnMaxWidth(stores) ? pagesInTopRow.rightPageIndex : pagesInTopRow.leftPageIndex;
      } else {
        pageIndex = pageIndexArray[pageAreaFractionArray.indexOf(highestAreaFractionVisible)];
      }
      if (goToPageIndex !== -1 && goToPageIndex !== pageIndex) {
        const goToPageIndexInArray = pageIndexArray.indexOf(goToPageIndex);
        if (
          goToPageIndexInArray !== -1 &&
          highestAreaFractionVisible - pageAreaFractionArray[goToPageIndexInArray] < 0.01
        ) {
          pageIndex = goToPageIndex;
        } else {
          stores.pagedViewState.setGoToPageIndex(-1);
        }
      }
    }
    pageIndex = pageIndex < 0 ? 0 : pageIndex;
  }

  return Math.min(totalPages - 1, pageIndex);
};

const getCurrentPageIndexUtil = stores => {
  const totalPages = getTotalPages(stores);
  if (totalPages > 1) {
    let { pageIndex, yInPage, pageBottomMargin, xInPage } = getPageBeforeOrAtOffset(stores, 0);
    if (isTwoColumnViewMode(stores)) {
      pageIndex = getCurrentPageIndexTwoColumn(stores, pageIndex, yInPage, pageBottomMargin, xInPage);
      return pageIndex;
    }
    let firstPageInView = pageIndex;
    if (yInPage !== -1) {
      let viewPortHeight = getViewPortHeightWithoutPagesViewScrollbar(stores);
      if (getIsInPlaceMode(stores)) {
        viewPortHeight = getDocumentVisibleHeight(stores);
      }
      if (pageIndex < totalPages - 1) {
        const firstPageHeight = getPageHeight(stores, pageIndex);
        viewPortHeight -= firstPageHeight - yInPage + PAGE_BOTTOM_MARGIN;
        if (viewPortHeight > 0) {
          const fractionOfFirstPageInView = (firstPageHeight - yInPage) / firstPageHeight;
          let fractionOfSecondPageInView = 0;
          let secondPageInView = pageIndex + 1;
          let secondPageHeight = getPageHeight(stores, secondPageInView);
          if (viewPortHeight > secondPageHeight) {
            fractionOfSecondPageInView = 1;
          } else {
            fractionOfSecondPageInView = viewPortHeight / secondPageHeight;
          }
          if (fractionOfFirstPageInView < fractionOfSecondPageInView) {
            return secondPageInView;
          }
        }
      }
    } else if (pageBottomMargin !== -1) {
      return Math.min(totalPages - 1, pageIndex + 1);
    } else {
      return pageIndex;
    }
    return firstPageInView;
  }

  return totalPages - 1;
};

export const getCurrentPageNumber = stores => getCurrentPageIndex(stores) + 1;

export const getPageIndexForSlideMode = stores => stores.documentStore.currPageIndexForSlideMode;

export const getPageIndexForFitOnePageMode = stores => stores.documentStore.currPageIndexForFitOnePageMode;

export const getCurrentPageIndex = stores => {
  if (isSinglePageMode(stores)) {
    return getPageIndexForSlideMode(stores);
  } else if (isFullScreenOrFitOnePageMode(stores)) {
    return getPageIndexForFitOnePageMode(stores);
  }
  return getCurrentPageIndexUtil(stores);
};

export const getCurrentRowIndex = stores => {
  const currentPageIndex = getCurrentPageIndex(stores);
  const rowIndex = getRowIndex(stores, currentPageIndex);

  return rowIndex;
};

export const isFirstPage = stores => getCurrentPageIndex(stores) === 0;

export const isLastPage = stores => getCurrentPageIndex(stores) === getTotalPages(stores) - 1;

export const getPageAndYPosClickedOn = (stores, offsetFromTop = 0) => {
  let { pageIndex, yInPage, xInPage } = getPageBeforeOrAtOffset(stores, offsetFromTop);
  if (yInPage === -1) {
    pageIndex = -1;
  }
  return { pageIndex, yInPage, xInPage };
};

// method to get page coordinates given screen coordinates.
export const getPageCoordinates = (stores, x, y) => {
  // get page element to get x and y in page.
  let elem = getHitElement(PageCanvasCss.parent, x, y);
  let xInPage = -1;
  let yInPage = -1;
  let pageIndex = -1;
  if (elem) {
    xInPage = x - elem.getBoundingClientRect().left - document.documentElement.scrollLeft;
    yInPage = y - elem.getBoundingClientRect().top - document.documentElement.scrollTop;
    pageIndex = Number(elem.dataset.index);
  }
  return { pageIndex, xInPage, yInPage };
};

export const getTouchPageIndex = (event, stores) => {
  let offsetFromTop = event.center.y;
  if (getIsInPlaceMode(stores)) {
    offsetFromTop -= getPagesViewYPos(stores);
  }
  const { pageIndex } = getPageAndYPosClickedOn(stores, offsetFromTop);

  return pageIndex;
};
